import { Capacitor, registerPlugin } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { push } from '../api/endpoints';
import { commentairesPath } from '../utils/routes';

/**
 * Les notifications distantes, cote iPhone.
 *
 * La moitie qui envoie existe depuis longtemps et tournait dans le vide :
 * cinq fonctions serveur s'arretaient toutes a la meme ligne, « aucun
 * jeton enregistre ». Voici la moitie qui en produit un.
 *
 * **La chaine, de bout en bout.** L'iPhone demande la permission a son
 * proprietaire ; Apple delivre un jeton d'appareil ; le SDK Firebase
 * embarque l'echange contre un jeton FCM ; on range celui-ci dans
 * `deviceTokens/{uid}` ; une fonction serveur le lit et demande l'envoi ;
 * Firebase signe avec la cle APNs et Apple livre.
 *
 * **Pourquoi le SDK Firebase natif et non le simple plugin de
 * notifications de Capacitor.** Ce dernier rend le jeton **Apple brut**,
 * alors que nos fonctions envoient par Firebase Cloud Messaging, qui
 * attend un jeton **FCM**. Les deux se ressemblent et ne s'echangent pas :
 * un jeton Apple donne a FCM est refuse sans que rien ne le dise. D'ou
 * `@capacitor-firebase/messaging`, qui embarque le SDK natif et fait
 * l'echange.
 *
 * **Sur le web, rien ne se passe** — et c'est voulu. Les notifications
 * d'un navigateur demandent une clé VAPID et un service worker, qu'on n'a
 * pas montes. Tout ici est donc garde par `isNativePlatform()`.
 */

/**
 * Le greffon qui garde la notification ayant lance l'application.
 *
 * Voir `ios/App/App/OuvertureNotif.swift` : quand on touche une
 * notification application fermee, l'evenement du greffon Firebase se
 * perd — son delegue n'est pas encore pose. La charge est alors reprise
 * des options de lancement, cote natif, et attend ici qu'on la reclame.
 */
const OuvertureNotif = registerPlugin<{
  enAttente(): Promise<{ data: Record<string, string> }>;
}>('OuvertureNotif');

/**
 * Ou mene une notification qu'on vient de toucher.
 *
 * Toutes ramenaient au fil d'accueil, quel que soit ce qu'elles
 * annoncaient : une demande d'ami laissait chercher l'ecran des demandes,
 * un message laissait retrouver la conversation. Or on touche une
 * notification pour aller quelque part.
 *
 * Le contenu utile voyage deja dans `data` depuis le premier envoi — les
 * fonctions le posent toutes. Il ne manquait que cette table.
 */
function destination(data: Record<string, unknown>): string | null {
  const lire = (cle: string) => (typeof data[cle] === 'string' ? (data[cle] as string) : null);

  switch (String(data.type ?? '')) {
    case 'message': {
      const conversationId = lire('conversationId');
      return conversationId ? `/app/messages/${conversationId}` : '/app/messages';
    }
    /* La demande vit dans l'ecran « Ajouter des amis », en tete. */
    case 'friend-request':
      return '/app/messages/friends';
    /*
      Une demande acceptee mene au profil de celui qui a dit oui, et non a
      l'ecran des demandes : il n'y a plus rien a y faire, et c'est lui
      qu'on veut voir.
    */
    case 'friend-accepted': {
      const fromId = lire('fromId');
      return fromId ? `/app/members/${fromId}` : '/app/messages/friends';
    }
    case 'post': {
      const postId = lire('postId');
      return postId ? commentairesPath(postId, lire('commentId')) : '/app/feed';
    }
    /* Une ancienne notification de trophee : l'ecran n'existe plus. */
    case 'trophee':
      return '/app/feed';
    /* La feuille de presence se remplit depuis la gestion de la sortie. */
    case 'feuille': {
      const eventId = lire('eventId');
      return eventId ? `/app/events/${eventId}/manage` : '/app/events';
    }
    /*
      Une inscription mene a la gestion de la sortie, pas a sa fiche :
      c'est la que l'organisateur valide, et c'est lui qu'on previent.
    */
    case 'inscription': {
      const eventId = lire('eventId');
      return eventId ? `/app/events/${eventId}/manage` : '/app/events';
    }
    case 'rappel':
    case 'seuil':
    /* Une demande acceptee mene a la fiche : on y trouve l'heure et le lieu. */
    case 'acceptation': {
      const eventId = lire('eventId');
      return eventId ? `/app/events/${eventId}` : '/app/events';
    }
    /* La sortie n'existe plus : on mene a celles qui restent. */
    case 'annulation':
      return '/app/events';
    /* Une demande a traiter, ou une adhesion acceptee : la page de l'association. */
    case 'asso': {
      const asso = lire('asso');
      return asso ? `/app/annuaire/associations/${asso}` : '/app/annuaire';
    }
    /* Un serveur vient d'ouvrir : la liste ou l'on change de serveur. */
    case 'serveur':
      return '/choose-spot';
    /*
      Un appel ne se navigue pas : CallKit a deja son ecran, et la bulle
      d'appel prend la suite dans l'application.
    */
    default:
      return null;
  }
}

/**
 * La destination d'une notification touchee avant que l'app soit prete.
 *
 * Toucher une notification peut **lancer** l'application : l'evenement
 * arrive alors bien avant que le routeur existe. On le garde de cote
 * plutot que de le perdre, exactement comme la reponse a un appel venue
 * de CallKit.
 */
let destinationEnAttente: string | null = null;

/** La fonction de navigation, quand le routeur est monte. */
let allerA: ((route: string) => void) | null = null;

/** Une seule ecoute des ouvertures, quoi qu'il arrive. */
let ouvertureInstallee = false;

/**
 * Pose l'ecoute des notifications touchees, le plus tot possible.
 *
 * Appelee depuis `main.tsx`, avant meme le premier rendu : entre le
 * lancement et le montage du routeur, il y a de quoi rater l'evenement.
 */
export function installerOuvertures(): void {
  if (!natif() || ouvertureInstallee) return;
  ouvertureInstallee = true;

  void FirebaseMessaging.addListener('notificationActionPerformed', (evenement) => {
    retenir(destination((evenement.notification?.data ?? {}) as Record<string, unknown>));
  });

  /*
    Et celle qui a lance l'application, que le greffon n'a pas vue.
    Reclamee une seule fois : le natif l'efface en la rendant.
  */
  void OuvertureNotif.enAttente()
    .then(({ data }) => retenir(destination(data ?? {})))
    .catch(() => undefined);
}

/** Range une destination, ou la donne au routeur s'il est deja la. */
function retenir(route: string | null): void {
  if (!route) return;
  if (allerA) allerA(route);
  else destinationEnAttente = route;
}

/**
 * Branche le routeur sur les ouvertures, et vide ce qui attendait.
 *
 * Rend de quoi se debrancher : le routeur meurt avec l'application, mais
 * un rendu de developpement le monte deux fois.
 */
export function ecouterOuvertures(navigate: (route: string) => void): () => void {
  allerA = navigate;

  if (destinationEnAttente) {
    const route = destinationEnAttente;
    destinationEnAttente = null;
    navigate(route);
  }

  return () => {
    allerA = null;
  };
}

/** Le jeton en cours, pour savoir lequel oublier a la deconnexion. */
let jetonCourant: string | null = null;

/** Une seule ecoute des renouvellements, meme si l'on se reconnecte. */
let ecouteInstallee = false;

function natif(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Demande la permission, si elle n'a pas deja ete accordee ou refusee.
 *
 * On ne la redemande jamais : iOS ne represente pas la question une
 * seconde fois, et insister ne ferait qu'appeler une fonction pour rien.
 * Un refus se rattrape dans les reglages du telephone, pas ici.
 */
async function permission(): Promise<boolean> {
  const deja = await FirebaseMessaging.checkPermissions();
  if (deja.receive === 'granted') return true;
  if (deja.receive === 'denied') return false;

  const demandee = await FirebaseMessaging.requestPermissions();
  return demandee.receive === 'granted';
}

/**
 * Les notifications sont-elles coupees dans les reglages du telephone ?
 *
 * On lit, on ne demande rien. `false` sur le web ou quand on ne sait pas :
 * mieux vaut ouvrir normalement l'ecran des reglages de l'application
 * que d'envoyer vers ceux du telephone sur une supposition.
 */
export async function notificationsCoupeesParLeTelephone(): Promise<boolean> {
  if (!natif()) return false;
  try {
    return (await FirebaseMessaging.checkPermissions()).receive === 'denied';
  } catch {
    return false;
  }
}

/**
 * Retire du centre de notifications celles d'une conversation qu'on lit.
 *
 * Ouvrir une conversation ne touchait qu'a la notification qu'on venait
 * de toucher, quand on passait par elle : les autres restaient en haut de
 * l'ecran, « untel vous a envoye un message » trois fois, pour des
 * messages deja lus. C'est ce que font toutes les messageries.
 *
 * Deux facons de les reconnaitre, une par systeme. Sur iPhone, les donnees
 * de l'envoi accompagnent la notification : on y lit la conversation. Sur
 * Android, le systeme ne les garde pas avec elle ; c'est l'etiquette posee
 * a l'envoi qui la nomme (`conversation-{id}`, voir `poster` cote serveur).
 *
 * Rien que des methodes du greffon deja present dans toutes les coquilles :
 * pas de natif a attendre. Un echec ne se voit pas, et ne doit rien
 * empecher d'autre.
 */
export async function effacerLesNotificationsDe(conversationId: string): Promise<void> {
  if (!natif()) return;
  try {
    const { notifications } = await FirebaseMessaging.getDeliveredNotifications();
    const etiquette = `conversation-${conversationId}`;
    const siennes = notifications.filter((notification) => {
      const donnees = (notification.data ?? {}) as Record<string, unknown>;
      return donnees.conversationId === conversationId || notification.tag === etiquette;
    });
    if (siennes.length === 0) return;
    await FirebaseMessaging.removeDeliveredNotifications({ notifications: siennes });
  } catch {
    // Au pire, elles restent : c'etait deja le cas.
  }
}

/**
 * Enregistre cet appareil pour le membre connecte.
 *
 * Appele a chaque ouverture de session : un jeton change tout seul —
 * reinstallation, restauration, expiration — et le reecrire ne coute
 * qu'une ligne. C'est aussi le seul moment ou l'on sait de qui il s'agit.
 */
export async function enregistrerCetAppareil(): Promise<void> {
  if (!natif()) return;

  /*
    L'ecoute d'abord, la demande ensuite.

    Le jeton FCM ne peut naitre qu'une fois qu'Apple a repondu, et Apple
    repond quand il veut : le plugin lui demande son jeton d'appareil au
    lancement, la reponse arrive quelques centaines de millisecondes plus
    tard. Reclamer le jeton FCM dans cet intervalle echoue — c'est ce qui
    s'est produit, et l'erreur etait avalee sans bruit.

    Cette ecoute est donc la vraie voie : elle part quand le jeton existe,
    tot ou tard, et elle sert aussi aux renouvellements — qu'Apple ne
    signale a personne d'autre que l'application. La demande directe qui
    suit n'est qu'un raccourci pour le cas ou tout est deja pret.
  */
  ecouter();

  try {
    if (!(await permission())) return;
    await reclamer();
  } catch {
    /*
      Une notification qui ne s'enregistre pas ne doit rien casser :
      l'application marche sans, comme elle l'a fait jusqu'ici. Et
      l'ecoute posee plus haut reste en place — c'est elle qui rattrapera.
    */
  }
}

/** Pose l'ecoute des jetons, une fois pour toutes. */
function ecouter(): void {
  if (ecouteInstallee) return;
  ecouteInstallee = true;
  void FirebaseMessaging.addListener('tokenReceived', (evenement) => {
    void poser(evenement.token);
  });
}

/**
 * Reclame le jeton, en laissant a Apple le temps de repondre.
 *
 * Cinq essais espaces d'une seconde : au-dela, ce n'est plus une course
 * mais un refus, et l'ecoute prendra le relais si le jeton finit par
 * arriver.
 */
async function reclamer(): Promise<void> {
  for (let essai = 0; essai < 5; essai += 1) {
    try {
      const { token } = await FirebaseMessaging.getToken();
      if (token) return poser(token);
    } catch {
      // Le jeton d'appareil n'est pas encore la : on attend.
    }
    await new Promise((suite) => setTimeout(suite, 1000));
  }
}

/** Range le jeton, sauf s'il est deja celui qu'on connait. */
async function poser(token: string | undefined): Promise<void> {
  if (!token || token === jetonCourant) return;
  jetonCourant = token;
  await push.register(token).catch(() => undefined);
}

/**
 * Oublie cet appareil.
 *
 * A la deconnexion : le telephone reste celui de quelqu'un, mais plus
 * celui de ce compte-la. Sans cet oubli, le membre suivant recevrait les
 * messages du precedent.
 */
export async function oublierCetAppareil(): Promise<void> {
  if (!natif() || !jetonCourant) return;
  const jeton = jetonCourant;
  jetonCourant = null;
  await push.forget(jeton).catch(() => undefined);
}
